import { PanelLeftOpen, X } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  PanelGroup,
  type ImperativePanelGroupHandle,
  type ImperativePanelHandle,
} from "react-resizable-panels";

import { Button } from "../../../../components/ui/button";
import {
  ResizableHandle,
  ResizablePanel,
} from "../../../../components/ui/resizable";

type HeaderTone = "pass" | "fail" | "neutral";

/**
 * Pixel-based collapse, ported from Forge (which runs react-resizable-panels v4
 * with pixel sizes). Our v2 only sizes in percentages, so we measure the group's
 * pixel width and convert: a collapsed pane is a fixed `PANE_RAIL_PX` rail, and
 * every pane collapses at the same `PANE_MIN_PX` — so the snap point is identical
 * across all panes (the symmetry v2's flat percentages don't give).
 */
const PANE_RAIL_PX = 36;
const PANE_MIN_PX = 240;

interface WorkpaperFoldOutProps {
  /** Header content (e.g. "Attribute D • Sample 3" + an outcome badge) — shown
   * in the middle pane when present, otherwise atop the viewer pane. */
  title: ReactNode;
  /** Bar rendered at the top of the middle pane, above its verdict header (e.g. a
   * Review/Agent view toggle). Full-width within the middle pane. */
  middleTopBar?: ReactNode;
  onClose: () => void;
  /** Left pane — the work-paper table. */
  gridPane: ReactNode;
  /** Middle pane — the cell's citation / audit-step content. Omit it to render
   * a two-pane `grid | viewer` layout (support cells). */
  middlePane?: ReactNode;
  /** Right pane — the document viewer. */
  viewerPane: ReactNode;
  /** Tints the header bar by outcome (light green / red). */
  headerTone?: HeaderTone;
  /** Whether the grid (workpaper) pane opens expanded. Lifted to the parent so it
   * survives the per-cell remount — clicking another cell keeps the workpaper
   * open if the reviewer had expanded it, instead of always re-collapsing. */
  gridExpanded?: boolean;
  onGridExpandedChange?: (expanded: boolean) => void;
  /** Pane proportions, lifted to the parent so a reviewer's resized split also
   * survives the per-cell remount — clicking another cell keeps the layout
   * instead of snapping back to the default thirds. */
  paneLayout?: number[] | null;
  onPaneLayoutChange?: (layout: number[]) => void;
  /** Parent-held vertical scroll offset for the grid pane, so the per-cell
   * remount doesn't snap the workpaper back to the top row — clicking a cell
   * never moves the grid. */
  gridScrollTopRef?: React.MutableRefObject<number>;
}

const HEADER_TONE: Record<HeaderTone, string> = {
  pass: "bg-emerald-50",
  fail: "bg-red-50",
  neutral: "",
};

/**
 * Inline fold-out shell — the Forge workpaper mechanic, no overlay, styled with
 * the product app's primitives. Each pane is a bordered card separated by a
 * transparent drag-gap. Attribute-test cells get three panes (`grid | audit
 * steps | doc viewer`); support cells get two (`grid | doc viewer`).
 *
 * Every pane is **drag-collapsible** with identical mechanics: dragging a divider
 * so a pane drops below `PANE_MIN_PX` snaps it to a `PANE_RAIL_PX` vertical rail
 * (its label + an expand button); clicking the rail restores it. The grid starts
 * collapsed so the steps + doc viewer get the room on open. `Esc` closes (so a
 * collapsed header pane never traps the close control).
 *
 * Expansion is driven through the group's `setLayout` (Forge's `expandPane`): a
 * rail re-expands by taking width only from the OTHER expanded panes, leaving
 * collapsed neighbours pinned at rail size — so expanding one never drags a
 * closed pane open.
 */
export function WorkpaperFoldOut({
  title,
  middleTopBar,
  onClose,
  gridPane,
  middlePane,
  viewerPane,
  headerTone = "neutral",
  gridExpanded,
  onGridExpandedChange,
  paneLayout,
  onPaneLayoutChange,
  gridScrollTopRef,
}: WorkpaperFoldOutProps) {
  const hasMiddle = middlePane != null;
  const gridStartsExpanded = !!gridExpanded;
  const groupRef = useRef<ImperativePanelGroupHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Restore the grid pane's lifted scroll offset whenever its scroll container
  // attaches — on the per-cell remount and again after a rail collapse/expand
  // (which unmounts the pane's children). Callback ref, so it fires on attach
  // rather than once per fold-out mount.
  const attachGridScroll = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && gridScrollTopRef?.current) {
        node.scrollTop = gridScrollTopRef.current;
      }
    },
    [gridScrollTopRef],
  );

  // Measure the group's pixel width so we can convert Forge's pixel sizes into
  // the percentages v2 wants — uniform across panes, so collapse is symmetric.
  // Layout effect: the first measurement must land before paint, or the panes
  // paint against the fallback collapsedSize/minSize and visibly re-snap once
  // the real width arrives (the doc viewer "jump" on per-cell remounts).
  const [groupWidth, setGroupWidth] = useState(0);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setGroupWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const collapsedSize = groupWidth > 0 ? (PANE_RAIL_PX / groupWidth) * 100 : 3;
  const minSize = groupWidth > 0 ? (PANE_MIN_PX / groupWidth) * 100 : 18;

  // Fill from the fold-out's top to the viewport bottom, measured dynamically.
  // The page header above the (h-screen) panel group varies in height, so a fixed
  // `100vh - Npx` over/undershoots and clips the bottom controls (Annotate, page
  // nav, tickmark strip). Measuring the top keeps them in frame on any layout.
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setAvailableHeight(Math.max(window.innerHeight - top - 16, 480));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // On open: grid collapsed to a rail, then split the rest 1/3 middle (steps) :
  // 2/3 doc viewer. Runs once, after the width is measured, so it sets the
  // default proportions each time the fold-out opens. Layout effect so the
  // settled layout applies in the same pre-paint pass as the measurement —
  // the panes never paint an intermediate split.
  const didInit = useRef(false);
  useLayoutEffect(() => {
    if (didInit.current || groupWidth === 0) return;
    const group = groupRef.current;
    if (!group) return;
    didInit.current = true;
    // A reviewer's resized layout (lifted to the parent) wins so it survives the
    // per-cell remount; otherwise fall back to the default proportions.
    if (paneLayout && paneLayout.length === (hasMiddle ? 3 : 2)) {
      group.setLayout(paneLayout);
    } else if (gridStartsExpanded) {
      // Grid opens expanded (persisted) → even shares across all open panes.
      const equal = 100 / (hasMiddle ? 3 : 2);
      group.setLayout(hasMiddle ? [equal, equal, equal] : [equal, equal]);
    } else {
      // Grid sits as a rail; split the rest 1/3 steps : 2/3 doc viewer.
      const rail = (PANE_RAIL_PX / groupWidth) * 100;
      const rest = Math.max(100 - rail, 0);
      group.setLayout(
        hasMiddle ? [rail, rest / 3, (rest * 2) / 3] : [rail, rest],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupWidth, hasMiddle, gridStartsExpanded]);

  // First-paint pane sizes, matching the layout the init effect settles on —
  // if they disagree, the panes paint at one split and visibly snap to another
  // once the width measurement lands (e.g. a support-cell viewer flashing wide
  // before shrinking to the even split). Only the collapsed-grid rail still
  // needs the post-measure correction, since its exact share is pixel-derived.
  const paneCount = hasMiddle ? 3 : 2;
  const defaultSizes: Record<"grid" | "middle" | "viewer", number> = (() => {
    if (paneLayout && paneLayout.length === paneCount) {
      return hasMiddle
        ? { grid: paneLayout[0], middle: paneLayout[1], viewer: paneLayout[2] }
        : { grid: paneLayout[0], middle: 0, viewer: paneLayout[1] };
    }
    if (gridStartsExpanded) {
      const equal = 100 / paneCount;
      return { grid: equal, middle: equal, viewer: equal };
    }
    // Grid starts collapsed: mount it AT the rail size so the group's own
    // initial layout computation lands it collapsed — an imperative collapse()
    // before that computation throws inside react-resizable-panels ("Panel
    // size not found"), unmounting the whole page.
    const rest = 100 - collapsedSize;
    return hasMiddle
      ? { grid: collapsedSize, middle: rest / 3, viewer: (rest * 2) / 3 }
      : { grid: collapsedSize, middle: 0, viewer: rest };
  })();

  // Panel order (matches the render order below); the group's layout array is
  // indexed the same way.
  const paneIds = hasMiddle ? ["grid", "middle", "viewer"] : ["grid", "viewer"];
  const expandTarget: Record<string, number> = {
    grid: hasMiddle ? 40 : 42,
    middle: 25,
    viewer: hasMiddle ? 35 : 58,
  };

  // Grid starts collapsed so the steps + doc viewer get the room on open —
  // unless the reviewer's lifted preference says to open it expanded.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    grid: !gridStartsExpanded,
  });

  // Report the grid pane's open/closed state up so it survives the per-cell
  // remount (ref-held callback so identity changes don't re-fire the effect).
  const onGridExpandedChangeRef = useRef(onGridExpandedChange);
  onGridExpandedChangeRef.current = onGridExpandedChange;
  useEffect(() => {
    onGridExpandedChangeRef.current?.(!collapsed.grid);
  }, [collapsed.grid]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Forge's expandPane: rebuild the whole layout so collapsed panes keep their
  // rail width and the remaining space goes to the expanding pane + the other
  // already-expanded panes (proportionally). Avoids the library's expand()
  // pulling a collapsed neighbour open.
  const expandPane = (id: string) => {
    const group = groupRef.current;
    if (!group) return;
    const layout = group.getLayout();
    const idx = paneIds.indexOf(id);
    if (idx < 0) return;

    const railTotal = paneIds.reduce(
      (sum, pid, i) => (pid !== id && collapsed[pid] ? sum + layout[i] : sum),
      0,
    );
    const available = Math.max(100 - railTotal, 0);
    const flexOther = paneIds
      .map((pid, i) => ({ pid, i }))
      .filter((o) => o.pid !== id && !collapsed[o.pid]);

    const next = [...layout];
    if (railTotal === 0) {
      // No pane is collapsed — every pane is open, so give them equal shares
      // (expanding the grid alongside the steps + viewer lands on even thirds).
      const equal = 100 / paneIds.length;
      for (let i = 0; i < next.length; i++) next[i] = equal;
    } else if (flexOther.length === 0) {
      next[idx] = available;
    } else {
      const want = Math.min(expandTarget[id] ?? 30, available);
      next[idx] = want;
      const flexTotal = flexOther.reduce((s, o) => s + layout[o.i], 0) || 1;
      const remainder = available - want;
      for (const o of flexOther) {
        next[o.i] = (layout[o.i] / flexTotal) * remainder;
      }
    }
    group.setLayout(next);
    setCollapsed((c) => ({ ...c, [id]: false }));
  };

  const setPaneCollapsed = (id: string, value: boolean) =>
    setCollapsed((c) => (c[id] === value ? c : { ...c, [id]: value }));

  const header = (
    <div className="flex shrink-0 items-center justify-between gap-2 border-b border-stone-200 px-3 py-2">
      <div
        className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-sm ${HEADER_TONE[headerTone]}`}
      >
        {title}
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        aria-label="Close"
        className="shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="w-full h-[calc(100vh-220px)] min-h-[480px]"
      style={availableHeight ? { height: availableHeight } : undefined}
    >
      <PanelGroup
        ref={groupRef}
        direction="horizontal"
        className="flex h-full w-full"
        onLayout={(layout) => onPaneLayoutChange?.(layout)}
      >
        <CollapsiblePane
          id="grid"
          order={1}
          // When starting collapsed this IS the rail size, so the group's
          // initial layout mounts it collapsed — no imperative collapse()
          // needed before the group has a layout (which throws).
          defaultSize={defaultSizes.grid}
          minSize={minSize}
          collapsedSize={collapsedSize}
          railLabel="Workpaper"
          startCollapsed={!gridStartsExpanded}
          collapsed={!!collapsed.grid}
          onCollapsedChange={(v) => setPaneCollapsed("grid", v)}
          onRailExpand={() => expandPane("grid")}
        >
          <FoldOutCard>
            <div
              ref={attachGridScroll}
              onScroll={(e) => {
                if (gridScrollTopRef) {
                  gridScrollTopRef.current = e.currentTarget.scrollTop;
                }
              }}
              className="h-full min-h-0 overflow-auto"
            >
              {gridPane}
            </div>
          </FoldOutCard>
        </CollapsiblePane>

        <FoldOutGap />

        {hasMiddle ? (
          <>
            <CollapsiblePane
              id="middle"
              order={2}
              defaultSize={defaultSizes.middle}
              minSize={minSize}
              collapsedSize={collapsedSize}
              railLabel="Steps"
              collapsed={!!collapsed.middle}
              onCollapsedChange={(v) => setPaneCollapsed("middle", v)}
              onRailExpand={() => expandPane("middle")}
            >
              <FoldOutCard>
                {middleTopBar}
                {header}
                <div className="min-h-0 flex-1">{middlePane}</div>
              </FoldOutCard>
            </CollapsiblePane>
            <FoldOutGap />
            <CollapsiblePane
              id="viewer"
              order={3}
              defaultSize={defaultSizes.viewer}
              minSize={minSize}
              collapsedSize={collapsedSize}
              railLabel="Document"
              collapsed={!!collapsed.viewer}
              onCollapsedChange={(v) => setPaneCollapsed("viewer", v)}
              onRailExpand={() => expandPane("viewer")}
            >
              <FoldOutCard>
                <div className="min-h-0 flex-1">{viewerPane}</div>
              </FoldOutCard>
            </CollapsiblePane>
          </>
        ) : (
          <CollapsiblePane
            id="viewer"
            order={2}
            defaultSize={defaultSizes.viewer}
            minSize={minSize}
            collapsedSize={collapsedSize}
            railLabel="Document"
            collapsed={!!collapsed.viewer}
            onCollapsedChange={(v) => setPaneCollapsed("viewer", v)}
            onRailExpand={() => expandPane("viewer")}
          >
            <FoldOutCard>
              {header}
              <div className="min-h-0 flex-1">{viewerPane}</div>
            </FoldOutCard>
          </CollapsiblePane>
        )}
      </PanelGroup>
    </div>
  );
}

/**
 * A drag-collapsible pane with uniform (pixel-derived) collapse sizing. Dragging
 * it below `minSize` snaps it to the `collapsedSize` rail — a vertical label +
 * expand button; the parent owns the collapsed state + the expand layout (so
 * rails never pull each other open). The `startCollapsed` pane collapses once on
 * mount.
 */
function CollapsiblePane({
  id,
  order,
  defaultSize,
  minSize,
  collapsedSize,
  railLabel,
  collapsed,
  onCollapsedChange,
  onRailExpand,
  startCollapsed = false,
  children,
}: {
  id: string;
  order: number;
  defaultSize: number;
  minSize: number;
  collapsedSize: number;
  railLabel: string;
  collapsed: boolean;
  onCollapsedChange: (value: boolean) => void;
  onRailExpand: () => void;
  startCollapsed?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<ImperativePanelHandle>(null);

  // Post-paint correction only: a `startCollapsed` pane already mounts at the
  // rail via `defaultSize`, so this is normally a no-op. It must NOT be a
  // layout effect — it would run before the PanelGroup computes its initial
  // layout, and collapse() then throws ("Panel size not found"), blanking the
  // page.
  useEffect(() => {
    if (startCollapsed) ref.current?.collapse();
    // Collapse once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResizablePanel
      ref={ref}
      id={id}
      order={order}
      defaultSize={defaultSize}
      minSize={minSize}
      collapsible
      collapsedSize={collapsedSize}
      onCollapse={() => onCollapsedChange(true)}
      onExpand={() => onCollapsedChange(false)}
    >
      {collapsed ? (
        <button
          type="button"
          onClick={onRailExpand}
          title={`Expand ${railLabel}`}
          className="flex h-full w-full flex-col items-center gap-2 overflow-hidden rounded-lg border border-stone-200 bg-white py-2.5 text-stone-400 transition-colors hover:text-stone-700"
        >
          <PanelLeftOpen className="h-4 w-4 shrink-0" />
          <span className="font-mono text-[10px] uppercase tracking-wider [writing-mode:vertical-rl]">
            {railLabel}
          </span>
        </button>
      ) : (
        children
      )}
    </ResizablePanel>
  );
}

/** A pane card: bordered, rounded, fills its panel; children own scrolling. */
function FoldOutCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-stone-200 bg-white">
      {children}
    </div>
  );
}

/** A transparent drag-gap between cards — the visible separation that gives the
 * panes their Forge-like aesthetic (vs. a hairline divider). */
function FoldOutGap() {
  return <ResizableHandle withHandle className="w-3 bg-transparent" />;
}
